import React, {useEffect, useRef, useState} from 'react';
import {useThemeProvider} from '../utils/ThemeContext';

import {chartColors} from './ChartjsConfig';
import {Chart, Filler, LinearScale, LineController, LineElement, PointElement, TimeScale, Tooltip,} from 'chart.js';
import 'chartjs-adapter-moment';

// Import utilities
import {formatValue, tailwindConfig} from '../utils/Utils';

Chart.register(LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, Tooltip);

function RealtimeChart({
                           data,
                           width,
                           height
                       }) {

    const [chart, setChart] = useState(null);
    const canvas = useRef(null);
    const chartValue = useRef(null);
    const chartDeviation = useRef(null);
    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {textColor, gridColor, tooltipTitleColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    useEffect(() => {
        const ctx = canvas.current;

        const newChart = new Chart(ctx, {
            type: 'line',
            data: data,
            options: {
                layout: {
                    padding: 20,
                },
                scales: {
                    y: {
                        border: {
                            display: false,
                        },
                        suggestedMin: 30,
                        suggestedMax: 80,
                        ticks: {
                            maxTicksLimit: 5,
                            callback: (value) => formatValue(value),
                            color: darkMode ? textColor.dark : textColor.light,
                        },
                        grid: {
                            color: darkMode ? gridColor.dark : gridColor.light,
                        },
                    },
                    x: {
                        type: 'time',
                        time: {
                            parser: 'hh:mm:ss',
                            unit: 'second',
                            tooltipFormat: 'MMM DD, H:mm:ss a',
                            displayFormats: {
                                second: 'H:mm:ss',
                            },
                        },
                        border: {
                            display: false,
                        },
                        grid: {
                            display: false,
                        },
                        ticks: {
                            autoSkipPadding: 48,
                            maxRotation: 0,
                            color: darkMode ? textColor.dark : textColor.light,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        titleFont: {
                            weight: '600',
                        },
                        callbacks: {
                            label: (context) => formatValue(context.parsed.y),
                        },
                        titleColor: darkMode ? tooltipTitleColor.dark : tooltipTitleColor.light,
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                    },
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                animation: false,
                maintainAspectRatio: false,
            },
        });
        setChart(newChart);
        return () => newChart.destroy();

    }, [data]);

    // Update header values
    useEffect(() => {
        const currentValue = data.datasets[0].data[data.datasets[0].data.length - 1];
        const previousValue = data.datasets[0].data[data.datasets[0].data.length - 2];
        const diff = ((currentValue - previousValue) / previousValue) * 100;
        chartValue.current.innerHTML = data.datasets[0].data[data.datasets[0].data.length - 1];
        if (diff < 0) {
            chartDeviation.current.style.backgroundColor = tailwindConfig().theme.colors.amber[500];
        } else {
            chartDeviation.current.style.backgroundColor = tailwindConfig().theme.colors.emerald[500];
        }
        chartDeviation.current.innerHTML = `${diff > 0 ? '+' : ''}${diff.toFixed(2)}%`;
    }, [data]);

    useEffect(
        () => {
            if (!chart) return;

            if (darkMode) {
                chart.options.scales.x.ticks.color = textColor.dark;
                chart.options.scales.y.ticks.color = textColor.dark;
                chart.options.scales.y.grid.color = gridColor.dark;
                chart.options.plugins.tooltip.titleColor = tooltipTitleColor.dark;
                chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
                chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
                chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
            } else {
                chart.options.scales.x.ticks.color = textColor.light;
                chart.options.scales.y.ticks.color = textColor.light;
                chart.options.scales.y.grid.color = gridColor.light;
                chart.options.plugins.tooltip.titleColor = tooltipTitleColor.light;
                chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
                chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
                chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
            }
            chart.update('none');
        },


        [currentTheme]
    );


    return (
        <React.Fragment>
            <div className="px-5 py-3">
                <div className="flex items-start">
                    <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 mr-2 tabular-nums">$<span
                        ref={chartValue}>57.81</span></div>
                    <div ref={chartDeviation} className="text-sm font-semibold text-white px-1.5 rounded-full"></div>
                </div>
            </div>
            <div className="grow">
                <canvas ref={canvas} width={width} height={height}></canvas>
            </div>
        </React.Fragment>
    );
}

export default RealtimeChart;